import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Bell, CalendarClock, Package } from 'lucide-react';
import toast from 'react-hot-toast';
import { listAppointments } from '../api/appointments';
import { listOrders, listProducts } from '../api/marketplace';
import { getErrorMessage } from '../api/response';
import PageHeader from '../components/PageHeader';
import StatusBadge from '../components/StatusBadge';
import Table from '../components/Table';
import { LoadingState } from '../components/PageState';
import { dateTime } from '../utils/format';

const Notificaciones = () => {
  const [appointments, setAppointments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const today = new Date().toISOString().slice(0, 10);
      const [appointmentData, paidOrders, readyOrders, productData] = await Promise.all([
        listAppointments({ from: `${today} 00:00:00`, to: `${today} 23:59:59`, limit: 100 }),
        listOrders({ status: 'paid', limit: 50 }),
        listOrders({ status: 'ready_for_pickup', limit: 50 }),
        listProducts({ limit: 100 })
      ]);
      setAppointments(appointmentData);
      setOrders([...paidOrders, ...readyOrders]);
      setProducts(productData);
    } catch (error) {
      toast.error(getErrorMessage(error, 'No se pudieron cargar alertas'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  const alerts = useMemo(() => {
    const lowStock = products
      .filter((product) => Number(product.stock || 0) <= 5)
      .map((product) => ({
        id: `stock-${product.id}`,
        type: 'Stock bajo',
        title: product.name,
        detail: `${product.stock} unidades disponibles`,
        priority: Number(product.stock || 0) === 0 ? 'Alta' : 'Media'
      }));

    const orderAlerts = orders.map((order) => ({
      id: `order-${order.id}-${order.status}`,
      type: 'Pedido pendiente',
      title: order.order_number,
      detail: `${order.customer_name} · ${order.status}`,
      priority: order.status === 'paid' ? 'Alta' : 'Media'
    }));

    const appointmentAlerts = appointments
      .filter((appointment) => appointment.status !== 'cancelled')
      .map((appointment) => ({
        id: `appointment-${appointment.id}`,
        type: 'Cita hoy',
        title: `Cita #${appointment.id}`,
        detail: dateTime(appointment.starts_at),
        priority: 'Normal'
      }));

    return [...orderAlerts, ...lowStock, ...appointmentAlerts];
  }, [appointments, orders, products]);

  const columns = [
    { key: 'type', label: 'Tipo' },
    { key: 'title', label: 'Referencia' },
    { key: 'detail', label: 'Detalle' },
    {
      key: 'priority',
      label: 'Prioridad',
      render: (row) => (
        <StatusBadge
          label={row.priority}
          color={row.priority === 'Alta' ? 'red' : row.priority === 'Media' ? 'amber' : 'blue'}
        />
      )
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alertas de gestión"
        description="Tareas operativas derivadas de citas, pedidos y stock para el equipo interno."
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <Bell className="h-5 w-5 text-blue-600" />
          <p className="mt-2 text-sm text-gray-500">Alertas activas</p>
          <p className="text-2xl font-bold text-gray-950">{alerts.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <Package className="h-5 w-5 text-amber-600" />
          <p className="mt-2 text-sm text-gray-500">Pedidos por entregar</p>
          <p className="text-2xl font-bold text-gray-950">{orders.length}</p>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <CalendarClock className="h-5 w-5 text-green-600" />
          <p className="mt-2 text-sm text-gray-500">Citas hoy</p>
          <p className="text-2xl font-bold text-gray-950">{appointments.length}</p>
        </div>
      </div>

      <section className="rounded-lg border border-gray-200 bg-white">
        {loading ? <div className="p-5"><LoadingState rows={5} /></div> : <Table columns={columns} data={alerts} emptyMessage="No hay alertas operativas" />}
      </section>

      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
        <div className="flex gap-3">
          <AlertTriangle className="h-5 w-5" />
          <p>Estas alertas son internas para operación. El envío push a clientes vive en “Push a clientes”.</p>
        </div>
      </div>
    </div>
  );
};

export default Notificaciones;
